import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  ArrowLeft, Check, Zap, Building2, Users, Phone,
  CreditCard, Package, Crown, Shield, BarChart3, Palette, Bot, AlertTriangle, Calendar, GraduationCap, Globe, Coins, Monitor
} from 'lucide-react';
import axios from 'axios';
import { TokenManagement } from '../components/TokenManagement';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CURRENCY_SYMBOLS = { ZAR: 'R', USD: '$' };

const PLAN_FEATURES = {
  starter: {
    icon: Zap,
    color: 'text-zinc-600',
    bg: 'bg-zinc-50',
    border: 'border-zinc-200',
    features: [
      '1 Sales Rep seat',
      '1 Admin seat (required)',
      '1 Manager seat (required)',
      '5 calls per rep/month',
      'Meeting Bot (20 min cap)',
      '1 manual override/meeting',
      'Google SSO',
    ],
    notIncluded: ['PDF Export', 'Team Analytics', 'Custom Branding'],
  },
  pro: {
    icon: Crown,
    color: 'text-brand-primary',
    bg: 'bg-brand-muted',
    border: 'border-brand-accent',
    badge: 'Most Popular',
    features: [
      '3 Sales Rep seats',
      '1 Admin seat (required)',
      '1 Manager seat (required)',
      '12 calls per rep/month',
      'Full Meeting Bot',
      '3 manual overrides/meeting',
      'PDF Export',
      'Basic Team Analytics',
      'Google SSO',
    ],
    notIncluded: ['Custom Branding'],
  },
  business: {
    icon: Building2,
    color: 'text-violet-600',
    bg: 'bg-violet-50',
    border: 'border-violet-200',
    features: [
      '10 Sales Rep seats',
      '1 Admin seat (required)',
      'Unlimited Manager seats',
      '15 calls per rep/month',
      'Full Meeting Bot',
      '5 manual overrides/meeting',
      'PDF Export',
      'Full Team Analytics + Leaderboard',
      'Custom Branding',
      'Google SSO',
    ],
    notIncluded: [],
  },
};

function PricingCard({ planId, plan, features, currentPlan, onSelect, loading, billingCycle, onCycleChange, currency, canPurchase = true }) {
  const isCurrent = currentPlan === planId;
  const isUpgrade = planId !== 'starter' && currentPlan === 'starter';
  const PlanIcon = features.icon;
  const sym = CURRENCY_SYMBOLS[currency] || 'R';

  const displayPrice = planId === 'starter' ? 0
    : billingCycle === 'annual' ? plan.price_annual : plan.price_monthly;
  const priceLabel = planId === 'starter' ? '/month'
    : billingCycle === 'annual' ? '/year (upfront)' : '/month';

  // Dynamic extra seat/training pricing from plan data
  const extraFeatures = [];
  if (planId !== 'starter') {
    extraFeatures.push(`${sym}${Math.round(plan.extra_rep_price)}/mo extra rep, ${sym}${Math.round(plan.extra_admin_price)}/mo extra admin or mgr`);
    const trainingPrice = planId === 'pro' ? Math.round(plan.price_monthly * 43) : Math.round(plan.price_monthly * 22);
    const trainingLabel = planId === 'pro'
      ? `${sym}${currency === 'USD' ? '1,667' : '30,000'}`
      : `${sym}${currency === 'USD' ? '3,056' : '55,000'}`;
    extraFeatures.push(`Optional Training & Setup — ${trainingLabel}`);
  }

  return (
    <Card
      className={`relative overflow-hidden transition-all ${
        isCurrent ? `${features.border} border-2 shadow-md` : 'border-zinc-200 hover:border-zinc-300'
      }`}
      data-testid={`plan-card-${planId}`}
    >
      {features.badge && (
        <div className="absolute top-0 right-0 bg-brand-primary text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
          {features.badge}
        </div>
      )}
      <CardHeader className="pb-2">
        <div className={`w-10 h-10 ${features.bg} rounded-lg flex items-center justify-center mb-3`}>
          <PlanIcon className={`w-5 h-5 ${features.color}`} />
        </div>
        <CardTitle className="text-lg">{plan.name}</CardTitle>
        <div className="flex items-baseline gap-1 mt-1">
          <span className="text-3xl font-bold text-zinc-900">
            {sym}{displayPrice.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </span>
          <span className="text-sm text-zinc-500">{priceLabel}</span>
        </div>
        {planId !== 'starter' && (
          <p className="text-xs text-zinc-400 mt-0.5">{currency}</p>
        )}
        {planId !== 'starter' && (
          <p className="text-xs text-emerald-600 mt-1 font-medium">Month-to-month — cancel anytime with 30 days notice</p>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          {features.features.map((f, i) => (
            <div key={i} className="flex items-start gap-2 text-sm">
              <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${features.color}`} />
              <span className="text-zinc-700">{f}</span>
            </div>
          ))}
          {extraFeatures.map((f, i) => (
            <div key={`extra-${i}`} className="flex items-start gap-2 text-sm">
              <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${features.color}`} />
              <span className="text-zinc-700">{f}</span>
            </div>
          ))}
          {features.notIncluded.map((f, i) => (
            <div key={`no-${i}`} className="flex items-start gap-2 text-sm text-zinc-400">
              <span className="w-4 h-4 mt-0.5 flex-shrink-0 text-center">-</span>
              <span>{f}</span>
            </div>
          ))}
        </div>
        {isCurrent ? (
          <Button disabled className="w-full" variant="outline" data-testid={`plan-current-${planId}`}>
            Current Plan
          </Button>
        ) : isUpgrade ? (
          <Button
            onClick={() => onSelect(planId, billingCycle)}
            disabled={loading || !canPurchase}
            title={!canPurchase ? 'Only admins can purchase plan upgrades' : undefined}
            className={`w-full ${planId === 'pro' ? 'bg-brand-primary hover:bg-brand-hover' : 'bg-violet-600 hover:bg-violet-700'}`}
            data-testid={`plan-upgrade-${planId}`}
          >
            {loading ? 'Processing...' : !canPurchase ? 'Admin only' : `Upgrade to ${plan.name}`}
          </Button>
        ) : (
          <Button variant="outline" className="w-full" disabled data-testid={`plan-select-${planId}`}>
            {planId === 'starter' ? 'Free Forever' : 'Downgrade'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function UsageDashboard({ usage, limits }) {
  if (!usage || !limits) return null;

  const repSeats = usage.rep_seats;
  const mgrSeats = usage.mgr_seats;

  return (
    <div className="space-y-6" data-testid="usage-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-zinc-200">
          <CardContent className="p-4">
            <div className="text-xs text-zinc-500 mb-1">Rep Seats</div>
            <div className="text-xl font-bold text-zinc-900">
              {repSeats.used} <span className="text-sm font-normal text-zinc-400">/ {repSeats.total}</span>
            </div>
          </CardContent>
        </Card>
        <Card className="border-zinc-200">
          <CardContent className="p-4">
            <div className="text-xs text-zinc-500 mb-1">Mgr/Admin Seats</div>
            <div className="text-xl font-bold text-zinc-900">
              {mgrSeats.used} <span className="text-sm font-normal text-zinc-400">/ {mgrSeats.total === -1 ? '∞' : mgrSeats.total}</span>
            </div>
          </CardContent>
        </Card>
        <Card className="border-zinc-200">
          <CardContent className="p-4">
            <div className="text-xs text-zinc-500 mb-1">Total Calls</div>
            <div className="text-xl font-bold text-zinc-900">{usage.total_calls}</div>
          </CardContent>
        </Card>
        <Card className="border-zinc-200">
          <CardContent className="p-4">
            <div className="text-xs text-zinc-500 mb-1">Bonus Calls</div>
            <div className="text-xl font-bold text-emerald-600">{usage.bonus_calls || 0}</div>
          </CardContent>
        </Card>
      </div>

      {usage.rep_details?.length > 0 && (
        <Card className="border-zinc-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Phone className="w-4 h-4 text-brand-primary" />
              Calls Per Rep (This Period)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {usage.rep_details.map((rep) => (
                <div key={rep.user_id} className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg">
                  <div>
                    <span className="text-sm font-medium text-zinc-900">{rep.name}</span>
                    <span className="text-xs text-zinc-400 ml-2">{rep.email}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-24 bg-zinc-200 rounded-full h-1.5">
                      <div
                        className={`h-1.5 rounded-full ${
                          rep.calls_used >= rep.calls_limit ? 'bg-red-500' : rep.calls_used > rep.calls_limit * 0.8 ? 'bg-amber-500' : 'bg-emerald-500'
                        }`}
                        style={{ width: `${Math.min((rep.calls_used / rep.calls_limit) * 100, 100)}%` }}
                      />
                    </div>
                    <span className={`text-sm font-mono ${rep.calls_remaining === 0 ? 'text-red-600' : 'text-zinc-600'}`}>
                      {rep.calls_used}/{rep.calls_limit}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default function BillingPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('usage');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [currency, setCurrency] = useState('ZAR');
  const [exchangeRate, setExchangeRate] = useState(18);

  // Auto-detect currency on mount
  useEffect(() => {
    const detectCurrency = async () => {
      try {
        const { data } = await axios.get(`${API}/billing/currency`);
        setCurrency(data.currency);
        setExchangeRate(data.exchange_rate);
      } catch (e) {
        console.error('Currency detection failed, defaulting to ZAR');
      }
    };
    detectCurrency();
  }, []);

  const canViewBilling = user?.role === 'admin' || user?.can_view_billing === true;

  const loadData = useCallback(async () => {
    try {
      const requests = [
        axios.get(`${API}/billing/plans?currency=${currency}`),
        axios.get(`${API}/billing/subscription`),
      ];
      if (canViewBilling) requests.push(axios.get(`${API}/billing/usage`));
      const [plansRes, subRes, usageRes] = await Promise.all(requests);
      setPlans(plansRes.data.plans);
      setSubscription(subRes.data);
      if (usageRes) setUsage(usageRes.data);
    } catch (e) {
      console.error('Failed to load billing data:', e);
    } finally {
      setLoading(false);
    }
  }, [currency, canViewBilling]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle Paystack callback return
  useEffect(() => {
    const reference = searchParams.get('reference') || searchParams.get('trxref');
    if (reference) {
      const verifyPayment = async () => {
        try {
          await axios.get(`${API}/billing/verify/${reference}`);
          loadData();
        } catch (e) {
          console.error('Verification error:', e);
        }
      };
      verifyPayment();
    }
  }, [searchParams, loadData]);

  const handleUpgrade = async (planId, cycle) => {
    setCheckoutLoading(true);
    try {
      const { data } = await axios.post(`${API}/billing/checkout`, {
        plan: planId,
        billing_cycle: cycle || billingCycle,
        origin_url: window.location.origin,
        currency,
      });
      window.location.href = data.url;
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to start checkout');
      setCheckoutLoading(false);
    }
  };

  const handleAddOn = async (addOnType, quantity = 1) => {
    setCheckoutLoading(true);
    try {
      const { data } = await axios.post(`${API}/billing/checkout`, {
        add_on_type: addOnType,
        quantity,
        origin_url: window.location.origin,
        currency,
      });
      window.location.href = data.url;
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to start checkout');
      setCheckoutLoading(false);
    }
  };

  const handleCancel = async () => {
    const msg = 'Are you sure you want to cancel? Your plan will remain active for 30 days after cancellation, then downgrade to Starter.';
    if (!window.confirm(msg)) return;

    setCheckoutLoading(true);
    try {
      const { data } = await axios.post(`${API}/billing/cancel`, {
        confirm: true,
      });

      alert(data.message || 'Cancellation confirmed.');
      loadData();
      setCheckoutLoading(false);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to cancel subscription');
      setCheckoutLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-brand-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  const currentPlan = subscription?.plan || 'starter';
  const isAdmin = user?.role === 'admin';
  const sym = CURRENCY_SYMBOLS[currency] || 'R';

  // Restricted view: managers (unless the org toggle allows) and sales reps
  // only see their own usage & token balance — no plans, spend or invoices.
  if (!canViewBilling) {
    return (
      <div className="min-h-screen bg-zinc-50" data-testid="billing-page-restricted">
        <header className="bg-white border-b border-zinc-200 px-6 py-4">
          <div className="max-w-3xl mx-auto flex items-center gap-4">
            <Link to="/" className="text-zinc-400 hover:text-zinc-600 transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-xl font-semibold text-zinc-900" style={{ fontFamily: 'var(--font-heading)' }}>
                My Usage
              </h1>
              <p className="text-sm text-zinc-500">Your call and token usage</p>
            </div>
          </div>
        </header>
        <div className="max-w-3xl mx-auto p-6 space-y-6">
          <TokenManagement mode="compact" currency={currency} isAdmin={false} />
          <Card className="border-zinc-200 shadow-none rounded-md">
            <CardContent className="pt-6 pb-6">
              <p className="text-sm text-zinc-600" data-testid="billing-restricted-note">
                Plans, invoices and payment details are managed by your organization's admin.
                If you need a plan change or more tokens, please contact them.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const toggleCurrency = () => {
    const newCurrency = currency === 'ZAR' ? 'USD' : 'ZAR';
    setCurrency(newCurrency);
  };

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="billing-page">
      <header className="bg-white border-b border-zinc-200 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to={user?.role === 'admin' ? '/admin' : '/'} className="text-zinc-400 hover:text-zinc-600 transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-xl font-semibold text-zinc-900" style={{ fontFamily: 'var(--font-heading)' }}>
                Billing & Usage
              </h1>
              <p className="text-sm text-zinc-500">
                {PLAN_FEATURES[currentPlan]?.icon && <span className="inline-flex items-center gap-1">
                  {plans?.[currentPlan]?.name || 'Starter'} Plan
                </span>}
              </p>
            </div>
          </div>
          <button
            onClick={toggleCurrency}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-zinc-200 bg-white hover:bg-zinc-50 text-sm font-medium text-zinc-700 transition-colors"
            data-testid="currency-toggle"
          >
            <Globe className="w-4 h-4 text-zinc-400" />
            {currency === 'ZAR' ? '🇿🇦 ZAR (R)' : '🇺🇸 USD ($)'}
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-6">
        {/* Tabs */}
        <div className="flex gap-1 bg-zinc-100 p-1 rounded-lg w-fit mb-6">
          {[
            { id: 'usage', label: 'Usage', icon: BarChart3 },
            { id: 'tokens', label: 'Tokens', icon: Coins },
            { id: 'plans', label: 'Plans', icon: CreditCard },
            ...(currentPlan !== 'starter' ? [{ id: 'addons', label: 'Add-Ons', icon: Package }] : []),
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                activeTab === tab.id ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'
              }`}
              data-testid={`billing-tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Usage Tab */}
        {activeTab === 'usage' && (
          <div className="space-y-6">
            <UsageDashboard usage={usage} limits={subscription} />

            {/* Subscription Details */}
            {subscription && currentPlan !== 'starter' && (
              <Card className="border-zinc-200" data-testid="subscription-info-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-brand-primary" />
                    Subscription Details
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="p-3 bg-zinc-50 rounded-lg">
                      <div className="text-xs text-zinc-500 mb-1">Billing</div>
                      <div className="text-sm font-semibold text-zinc-900">Monthly (no commitment)</div>
                    </div>
                    <div className="p-3 bg-zinc-50 rounded-lg">
                      <div className="text-xs text-zinc-500 mb-1">Cancellation</div>
                      <div className="text-sm font-semibold text-zinc-900">30 days written notice</div>
                    </div>
                    <div className="p-3 bg-zinc-50 rounded-lg">
                      <div className="text-xs text-zinc-500 mb-1">Status</div>
                      <div className={`text-sm font-semibold ${
                        subscription.status === 'active' ? 'text-emerald-600' :
                        subscription.status === 'cancelling' ? 'text-amber-600' :
                        subscription.status === 'past_due' ? 'text-amber-600' : 'text-red-600'
                      }`}>
                        {subscription.status === 'active' ? 'Active' :
                         subscription.status === 'cancelling' ? 'Cancelling (30-day notice)' :
                         subscription.status === 'past_due' ? 'Past Due' : subscription.status}
                      </div>
                    </div>
                  </div>

                  {subscription.cancellation_effective_date && subscription.status === 'cancelling' && (
                    <div className="mt-3 p-3 bg-amber-50 border border-amber-100 rounded-lg">
                      <p className="text-sm text-amber-800">
                        Your plan remains active until <strong>{new Date(subscription.cancellation_effective_date).toLocaleDateString()}</strong>. After that, you'll be downgraded to Starter.
                      </p>
                    </div>
                  )}

                  {isAdmin && subscription.status !== 'cancelling' && (
                    <div className="mt-4 pt-4 border-t border-zinc-100">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => handleCancel()}
                        disabled={checkoutLoading}
                        data-testid="cancel-subscription-btn"
                      >
                        Cancel Subscription (30-day notice)
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Tokens Tab */}
        {activeTab === 'tokens' && (
          <TokenManagement mode="full" currency={currency} isAdmin={isAdmin} />
        )}

        {/* Plans Tab */}
        {activeTab === 'plans' && plans && (
          <div className="space-y-6">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center" data-testid="cancellation-info">
              <p className="text-sm text-emerald-800">
                All plans are <strong>month-to-month</strong> — no long-term commitment. Cancel anytime with <strong>30 days written notice</strong>.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {Object.entries(plans).map(([planId, plan]) => (
                <PricingCard
                  key={planId}
                  planId={planId}
                  plan={plan}
                  features={PLAN_FEATURES[planId]}
                  currentPlan={currentPlan}
                  onSelect={handleUpgrade}
                  loading={checkoutLoading}
                  billingCycle={'monthly'}
                  onCycleChange={setBillingCycle}
                  currency={currency}
                  canPurchase={isAdmin}
                />
              ))}
            </div>

            {/* Desktop helper — coming soon teaser */}
            <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-4 flex items-start gap-3" data-testid="desktop-helper-teaser">
              <Monitor className="w-5 h-5 text-zinc-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1 text-sm">
                <p className="font-medium text-zinc-900 mb-0.5">
                  Mac &amp; Windows Desktop Helper — coming soon
                </p>
                <p className="text-xs text-zinc-500">
                  A small background app that auto-captures Zoom / Teams / Meet calls — no bot in the meeting, nothing to start manually.
                  Currently in beta development.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Add-Ons Tab */}
        {activeTab === 'addons' && currentPlan !== 'starter' && plans && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-zinc-900">Extra Seats</h2>
            <div className="grid md:grid-cols-3 gap-4">
              <Card className="border-zinc-200">
                <CardContent className="p-5 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <Users className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <div className="font-medium text-zinc-900">Extra Rep Seat</div>
                      <div className="text-sm text-zinc-500">
                        {sym}{Math.round(plans[currentPlan]?.extra_rep_price || 0)}/month per seat
                      </div>
                    </div>
                  </div>
                  <Button
                    onClick={() => handleAddOn('extra_rep')}
                    disabled={checkoutLoading || !isAdmin}
                    size="sm"
                    data-testid="buy-extra-rep"
                  >
                    Add Seat
                  </Button>
                </CardContent>
              </Card>
              <Card className="border-zinc-200">
                <CardContent className="p-5 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-brand-accent-muted rounded-lg flex items-center justify-center">
                      <Shield className="w-5 h-5 text-brand-primary" />
                    </div>
                    <div>
                      <div className="font-medium text-zinc-900">Extra Admin Seat</div>
                      <div className="text-sm text-zinc-500">
                        {sym}{Math.round(plans[currentPlan]?.extra_admin_price || 0)}/month per seat
                      </div>
                    </div>
                  </div>
                  <Button
                    onClick={() => handleAddOn('extra_admin')}
                    disabled={checkoutLoading || !isAdmin}
                    size="sm"
                    data-testid="buy-extra-admin"
                  >
                    Add Seat
                  </Button>
                </CardContent>
              </Card>
              <Card className="border-zinc-200">
                <CardContent className="p-5 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                      <Shield className="w-5 h-5 text-violet-600" />
                    </div>
                    <div>
                      <div className="font-medium text-zinc-900">Extra Manager Seat</div>
                      <div className="text-sm text-zinc-500">
                        {sym}{Math.round(plans[currentPlan]?.extra_mgr_price || 0)}/month per seat
                      </div>
                    </div>
                  </div>
                  <Button
                    onClick={() => handleAddOn('extra_mgr')}
                    disabled={checkoutLoading || !isAdmin || currentPlan === 'business'}
                    size="sm"
                    variant={currentPlan === 'business' ? 'outline' : 'default'}
                    data-testid="buy-extra-mgr"
                  >
                    {currentPlan === 'business' ? 'Unlimited' : 'Add Seat'}
                  </Button>
                </CardContent>
              </Card>
            </div>

            <h2 className="text-lg font-semibold text-zinc-900 mt-8">Call Packs</h2>
            <div className="grid md:grid-cols-3 gap-4">
              <Card className="border-zinc-200">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-brand-accent-muted rounded-lg flex items-center justify-center">
                      <Phone className="w-5 h-5 text-brand-primary" />
                    </div>
                    <div>
                      <div className="font-medium text-zinc-900">25 Calls</div>
                      <div className="text-sm text-zinc-500">{sym}{currency === 'USD' ? Math.round(599 / exchangeRate) : '599'} one-time</div>
                    </div>
                  </div>
                  <Button
                    onClick={() => handleAddOn('pack_25')}
                    disabled={checkoutLoading || !isAdmin}
                    className="w-full"
                    variant="outline"
                    data-testid="buy-pack-25"
                  >
                    Buy Pack
                  </Button>
                </CardContent>
              </Card>
              <Card className="border-zinc-200">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-brand-accent-muted rounded-lg flex items-center justify-center">
                      <Phone className="w-5 h-5 text-brand-primary" />
                    </div>
                    <div>
                      <div className="font-medium text-zinc-900">50 Calls</div>
                      <div className="text-sm text-zinc-500">{sym}{currency === 'USD' ? Math.round(999 / exchangeRate) : '999'} one-time</div>
                    </div>
                  </div>
                  <Button
                    onClick={() => handleAddOn('pack_50')}
                    disabled={checkoutLoading || !isAdmin}
                    className="w-full"
                    variant="outline"
                    data-testid="buy-pack-50"
                  >
                    Buy Pack
                  </Button>
                </CardContent>
              </Card>
              <Card className="border-zinc-200">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                      <Zap className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <div className="font-medium text-zinc-900">Power Caller</div>
                      <div className="text-sm text-zinc-500">{sym}{currency === 'USD' ? Math.round(399 / exchangeRate) : '399'}/user — +15 calls for one rep</div>
                    </div>
                  </div>
                  <Button
                    onClick={() => handleAddOn('power_caller')}
                    disabled={checkoutLoading || !isAdmin}
                    className="w-full"
                    variant="outline"
                    data-testid="buy-power-caller"
                  >
                    Add Power Caller
                  </Button>
                </CardContent>
              </Card>
            </div>

            <h2 className="text-lg font-semibold text-zinc-900 mt-8">Training & Setup</h2>
            <Card className="border-zinc-200" data-testid="training-package-card">
              <CardContent className="p-5 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <GraduationCap className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <div className="font-medium text-zinc-900">Training & Setup Package</div>
                    <div className="text-sm text-zinc-500">
                      Dedicated onboarding, custom framework configuration, team training sessions, and priority support setup
                    </div>
                    <div className="text-sm font-semibold text-indigo-600 mt-1">
                      {sym}{currency === 'USD'
                        ? (currentPlan === 'pro' ? Math.round(30000 / exchangeRate).toLocaleString() : Math.round(55000 / exchangeRate).toLocaleString())
                        : (currentPlan === 'pro' ? '30,000' : '55,000')
                      } one-time
                    </div>
                  </div>
                </div>
                <Button
                  onClick={() => handleAddOn('training_setup')}
                  disabled={checkoutLoading || !isAdmin || subscription?.training_purchased}
                  size="sm"
                  className={subscription?.training_purchased ? '' : 'bg-indigo-600 hover:bg-indigo-700'}
                  variant={subscription?.training_purchased ? 'outline' : 'default'}
                  data-testid="buy-training-setup"
                >
                  {subscription?.training_purchased ? 'Purchased' : 'Buy Package'}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
